import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { logEvent } from '../../analytics';
import toast from 'react-hot-toast';
import generatePDF, { Margin } from 'react-to-pdf';

const DownloadPDFButton = ({ pdfRef, eventCategory, disabled }) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    // Add print stylesheet
    const style = document.createElement('style');
    style.id = 'pdf-print-styles';
    style.textContent = `
      @media print {
        body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        * {
          -webkit-font-smoothing: antialiased !important;
          -moz-osx-font-smoothing: grayscale !important;
          text-rendering: optimizeLegibility !important;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      const styleElement = document.getElementById('pdf-print-styles');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);

  const handleDownloadPDF = async () => {
    logEvent(eventCategory, 'PDF');
    setIsDownloading(true);

    const container = pdfRef.current;

    // Save original styles
    const originalOverflow = container.style.overflow;
    const originalHeight = container.style.height;
    const originalFontFamily = container.style.fontFamily;
    const originalFontSize = container.style.fontSize;
    const originalFontSmoothing = container.style.webkitFontSmoothing;
    const originalTextRendering = container.style.textRendering;

    try {
      // Expand container and set optimal font settings for PDF generation
      container.style.overflow = 'visible';
      container.style.height = 'auto';
      container.style.fontFamily = 'Arial, Helvetica, sans-serif';
      container.style.fontSize = '16px';
      container.style.webkitFontSmoothing = 'antialiased';
      container.style.mozOsxFontSmoothing = 'grayscale';
      container.style.textRendering = 'optimizeLegibility';

      // Add print-specific class
      container.classList.add('pdf-print-mode');

      // Pre-process images to handle CORS
      const images = container.getElementsByTagName('img');
      await Promise.all(
        Array.from(images).map(async (img) => {
          if (img.src) {
            try {
              const response = await fetch(img.src, {
                mode: 'cors',
                credentials: 'same-origin'
              });
              const blob = await response.blob();
              const reader = new FileReader();
              await new Promise((resolve, reject) => {
                reader.onload = () => {
                  img.src = reader.result;
                  resolve();
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            } catch (error) {
              console.warn('Failed to process image:', img.src, error);
            }
          }
        })
      );

      const now = new Date();
      const formattedDate = now.toISOString().slice(0, 10);
      const formattedTime = now.toTimeString().slice(0, 5).replace(':', '-');
      const filename = `document_${formattedDate}_${formattedTime}.pdf`;
      const pdfOptions = {
        filename,
        page: { margin: Margin.MEDIUM },
        method: 'save',
        resolution: 3,
        canvas: {
          scale: 3,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: true,
          imageTimeout: 0,
          removeContainer: true,
          foreignObjectRendering: true
        },
        html2canvas: {
          scale: 3,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: true,
          imageTimeout: 0,
          removeContainer: true,
          foreignObjectRendering: true
        }
      };

      await generatePDF(pdfRef, pdfOptions);
      toast.success(t('download_pdf.success'));
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(t('download_pdf.error'));
    } finally {
      // Restore original styles
      container.style.overflow = originalOverflow;
      container.style.height = originalHeight;
      container.style.fontFamily = originalFontFamily;
      container.style.fontSize = originalFontSize;
      container.style.webkitFontSmoothing = originalFontSmoothing;
      container.style.textRendering = originalTextRendering;
      container.classList.remove('pdf-print-mode');
      setIsDownloading(false);
    }
  };

  return (
    <Button
      icon={<DownloadOutlined />}
      onClick={handleDownloadPDF}
      disabled={isDownloading || disabled}
    >
      {t('download_pdf.label')}
    </Button>
  );
};

export default DownloadPDFButton;
